/**
 * Reporte de errores del cliente (frontend).
 *
 * Backend: POST {API_BASE_URL}/reportclienterror
 * Docs: ver contrato en repositorio backend (migrations/016_client_errors.sql).
 *
 * Activar envío (develop y prod; en localhost nunca se POST /reportclienterror):
 *   REACT_APP_CLIENT_ERROR_REPORTING_ENABLED=true
 *
 * Versión de la app (campo app_version en client_errors):
 *   REACT_APP_VERSION=Development | Production (workflows develop / main)
 *
 * Override opcional del endpoint:
 *   REACT_APP_CLIENT_ERROR_ENDPOINT
 *
 * Cola local: `terrio_client_error_queue` (reintento en 429 / 5xx; 400 se descarta).
 *
 * ARCHIVOS RELACIONADOS
 * ---------------------
 * - src/Components/ErrorBoundary/         → UI de fallback + captura en render
 * - src/Utils/setupGlobalErrorHandlers.js → window.onerror + unhandledrejection
 * - src/index.js                        → inicializa handlers y flush de cola
 * - src/App.js                          → monta <ErrorBoundary> en el router
 */
import { API_BASE_URL } from '../Utils/constants';
import { getValidAccessToken } from '../Access/services/authService';
// import { pushAnalyticsEvent } from '../analytics/analytics';

const CLIENT_ERROR_ENDPOINT =
  process.env.REACT_APP_CLIENT_ERROR_ENDPOINT || `${API_BASE_URL}/reportclienterror`;

const REPORTING_ENABLED = process.env.REACT_APP_CLIENT_ERROR_REPORTING_ENABLED === 'true';

/**
 * `npm start` no debe escribir en client_errors: el ruido local ensucia
 * Errores. develop (`dev-app`) y prod sí, si el flag está prendido.
 *
 * @param {string} [hostname]
 * @returns {boolean}
 */
export function isLocalhostHostname(hostname) {
  const host = String(hostname || '').toLowerCase();
  return host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host === '::1';
}

/**
 * @param {{ hostname?: string, reportingEnabled?: boolean }} [options]
 * @returns {boolean}
 */
export function shouldPersistClientError({
  hostname,
  reportingEnabled = REPORTING_ENABLED,
} = {}) {
  if (isLocalhostHostname(hostname)) return false;
  return reportingEnabled === true;
}

function currentHostname() {
  return typeof window !== 'undefined' && window.location
    ? window.location.hostname
    : '';
}

const LOCAL_QUEUE_KEY = 'terrio_client_error_queue';
const MAX_QUEUE_SIZE = 100;
const REPORT_THROTTLE_MS = 5000;

const VALID_SOURCES = new Set(['error_boundary', 'window_error', 'unhandled_rejection']);

let lastReportSignature = '';
let lastReportAt = 0;

const getHeaders = async () => {
  const token = await getValidAccessToken();
  const headers = { 'Content-Type': 'application/json' };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

const getRoutePath = () => window.location.hash.replace(/^#/, '') || '/';

const buildErrorContext = () => ({
  url: window.location.href,
  route: getRoutePath(),
  user_agent: navigator.userAgent,
  timestamp: new Date().toISOString(),
  app_version: process.env.REACT_APP_VERSION || null,
});

const normalizeError = (error) => {
  if (error instanceof Error) {
    return {
      message: error.message || 'Unknown error',
      stack: error.stack || null,
      name: error.name || 'Error',
    };
  }

  return {
    message: String(error ?? 'Unknown error'),
    stack: null,
    name: 'NonErrorThrown',
  };
};

const normalizeMetadata = (metadata = {}) => {
  const normalized = { ...metadata };

  if (normalized.errorReferenceId != null && normalized.error_reference_id == null) {
    normalized.error_reference_id = normalized.errorReferenceId;
    delete normalized.errorReferenceId;
  }

  if (normalized.resetKey != null && normalized.reset_key == null) {
    normalized.reset_key = normalized.resetKey;
    delete normalized.resetKey;
  }

  return Object.keys(normalized).length > 0 ? normalized : undefined;
};

const buildApiPayload = ({
  message,
  stack,
  name,
  componentStack,
  component_stack,
  source,
  metadata,
  ...context
}) => {
  const payload = {
    message,
    source,
    url: context.url,
    route: context.route,
    user_agent: context.user_agent ?? context.userAgent,
    timestamp: context.timestamp,
    app_version: context.app_version ?? context.appVersion ?? null,
  };

  if (stack) payload.stack = stack;
  if (name) payload.name = name;

  const componentStackValue = component_stack ?? componentStack;
  if (componentStackValue) payload.component_stack = componentStackValue;

  const normalizedMetadata = normalizeMetadata(metadata);
  if (normalizedMetadata) payload.metadata = normalizedMetadata;

  return payload;
};

const readLocalQueue = () => {
  try {
    const raw = localStorage.getItem(LOCAL_QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeLocalQueue = (queue) => {
  try {
    localStorage.setItem(LOCAL_QUEUE_KEY, JSON.stringify(queue.slice(-MAX_QUEUE_SIZE)));
  } catch {
    // Si localStorage está lleno o bloqueado, ignoramos el guardado local.
  }
};

const enqueueLocalError = (payload) => {
  const queue = readLocalQueue();
  queue.push(payload);
  writeLocalQueue(queue);
};

const shouldThrottleReport = (payload) => {
  const signature = `${payload.source}|${payload.message}|${payload.route}`;
  const now = Date.now();

  if (signature === lastReportSignature && now - lastReportAt < REPORT_THROTTLE_MS) {
    return true;
  }

  lastReportSignature = signature;
  lastReportAt = now;
  return false;
};

const sendErrorToBackend = async (payload) => {
  const apiPayload = buildApiPayload(payload);

  let response;
  try {
    response = await fetch(CLIENT_ERROR_ENDPOINT, {
      method: 'POST',
      headers: await getHeaders(),
      body: JSON.stringify(apiPayload),
    });
  } catch {
    return { success: false, retryable: true };
  }

  if (response.status === 201 || response.ok) {
    return { success: true, retryable: false };
  }

  if (response.status === 400) {
    return { success: false, retryable: false };
  }

  if (response.status === 429 || response.status >= 500) {
    return { success: false, retryable: true };
  }

  return { success: false, retryable: false };
};

const trySendOrQueue = async (payload) => {
  if (!shouldPersistClientError({ hostname: currentHostname() })) {
    if (process.env.NODE_ENV === 'development') {
      console.error(
        isLocalhostHostname(currentHostname())
          ? '[clientErrorService] Error local (no se graba en la DB):'
          : '[clientErrorService] Error registrado localmente (reporting deshabilitado):',
        payload,
      );
    }
    return;
  }

  const result = await sendErrorToBackend(payload);

  if (result.success) {
    return;
  }

  if (result.retryable) {
    enqueueLocalError(payload);
    if (process.env.NODE_ENV === 'development') {
      console.error('[clientErrorService] Error encolado para reintento:', payload);
    }
    return;
  }

  if (process.env.NODE_ENV === 'development') {
    console.error('[clientErrorService] Error rechazado por el backend (no se reencola):', payload);
  }
};

/**
 * Registra un error del cliente y lo envía a POST /reportclienterror.
 *
 * Body snake_case (el backend no acepta user_id en body; usa JWT si hay sesión).
 */
export const reportClientError = async ({
  error,
  errorInfo = null,
  source = 'error_boundary',
  metadata = {},
} = {}) => {
  if (!VALID_SOURCES.has(source)) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[clientErrorService] source inválido: ${source}`);
    }
    return;
  }

  const normalized = normalizeError(error);
  const payload = buildApiPayload({
    ...normalized,
    component_stack: errorInfo?.componentStack || null,
    source,
    metadata,
    ...buildErrorContext(),
  });

  if (shouldThrottleReport(payload)) {
    return;
  }

  // GA4 / GTM deshabilitado por ahora.
  // pushAnalyticsEvent('client_error', {
  //   source,
  //   route: payload.route,
  //   error_name: payload.name,
  // });

  await trySendOrQueue(payload);
};

export const flushQueuedClientErrors = async () => {
  if (!shouldPersistClientError({ hostname: currentHostname() })) return;

  const queue = readLocalQueue();
  if (!queue.length) return;

  const remaining = [];

  for (const payload of queue) {
    const result = await sendErrorToBackend(payload);

    if (!result.success && result.retryable) {
      remaining.push(payload);
    }
  }

  writeLocalQueue(remaining);
};

export const getQueuedClientErrors = () => readLocalQueue();

export const isClientErrorReportingEnabled = () => (
  shouldPersistClientError({ hostname: currentHostname() })
);
